import { forwardRef, type InputHTMLAttributes, useRef } from 'react';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';

export interface NumberInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size' | 'value' | 'onChange'
> {
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly invalid?: boolean;
  readonly stepperLabel?: { readonly inc: string; readonly dec: string };
}

const SIZE = {
  sm: { wrap: 'h-[34px]', text: 'text-[12.5px]', btn: 'w-8' },
  md: { wrap: 'h-10', text: 'text-[13.5px]', btn: 'w-9' },
  lg: { wrap: 'h-12', text: 'text-[15px]', btn: 'w-10' },
} as const;

function clamp(v: number, min: number | undefined, max: number | undefined): number {
  if (min !== undefined && v < min) return min;
  if (max !== undefined && v > max) return max;
  return v;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  {
    value,
    onChange,
    min,
    max,
    step = 1,
    size = 'md',
    invalid,
    disabled,
    className,
    stepperLabel,
    readOnly,
    ...rest
  },
  ref,
) {
  const { t } = useI18n();
  const innerRef = useRef<HTMLInputElement>(null);
  const sz = SIZE[size];
  const setValue = (v: number) => onChange(clamp(v, min, max));
  return (
    <div
      className={cn(
        'inline-flex min-w-0 items-stretch w-full rounded-lg bg-surface [@media(pointer:coarse)]:min-h-11',
        'border border-ink-3/80 overflow-hidden',
        'transition-[box-shadow,border-color] duration-(--duration-tap) ease-(--ease-out) motion-reduce:transition-none',
        'focus-within:border-accent-text focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent-text',
        invalid && 'border-danger focus-within:border-danger focus-within:ring-danger/25',
        disabled && 'opacity-45',
        sz.wrap,
        className,
      )}
    >
      <button
        type="button"
        disabled={disabled || readOnly || (min !== undefined && value <= min)}
        onClick={() => setValue(value - step)}
        aria-label={stepperLabel?.dec ?? t('common:forms.decrement', { defaultValue: 'Decrement' })}
        className={cn(
          sz.btn,
          'inline-flex shrink-0 items-center justify-center [@media(pointer:coarse)]:min-w-11 text-ink-2 hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent-text',
        )}
      >
        −
      </button>
      <input
        ref={(node) => {
          innerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
        }}
        type="number"
        inputMode="numeric"
        value={Number.isFinite(value) ? value : ''}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={invalid || undefined}
        onChange={(e) => {
          const next = e.target.valueAsNumber;
          if (Number.isNaN(next)) return;
          setValue(next);
        }}
        className={cn(
          'flex-1 min-w-0 text-center bg-transparent text-ink tabular-nums font-semibold [@media(pointer:coarse)]:text-base',
          'border-x border-hairline focus:outline-none disabled:cursor-not-allowed',
          '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
          sz.text,
        )}
        {...rest}
      />
      <button
        type="button"
        disabled={disabled || readOnly || (max !== undefined && value >= max)}
        onClick={() => setValue(value + step)}
        aria-label={stepperLabel?.inc ?? t('common:forms.increment', { defaultValue: 'Increment' })}
        className={cn(
          sz.btn,
          'inline-flex shrink-0 items-center justify-center [@media(pointer:coarse)]:min-w-11 text-ink-2 hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent-text',
        )}
      >
        +
      </button>
    </div>
  );
});
